// T-177 — « Générer l'attestation » (attestation sur l'honneur CEE) dans le dossier OMV.
// POST /api/metier/omv/dossiers/{id}/attestation-honneur : le serveur génère le PDF pré-rempli ET le range dans les pièces du dossier.
// Bouton SECONDAIRE de la charte : contour --oh-accent, fond transparent, texte --oh-accent-text (lisible en clair ET en foncé).
// Aucune couleur en dur : paires --oh-error-* / --oh-success-* pour les messages. Les données manquantes sont LISTÉES avec l'écran où les renseigner.
import { useState } from "react";
import { FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { enteteTenant } from "../../utils/enteteTenant";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function AttestationHonneurBouton({ dossierId, tenantId, onUpdate, disabled = false }) {
  const [enCours, setEnCours] = useState(false);
  const [refus, setRefus] = useState(null);       // { message, manquants[] } — dernier refus lisible
  const [fait, setFait] = useState(null);         // nom de la pièce rangée

  const generer = async () => {
    setEnCours(true); setRefus(null); setFait(null);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/api/metier/omv/dossiers/${dossierId}/attestation-honneur`, {
        method: "POST", credentials: "include", body: "{}",
        // Anti-CSRF des routes d'écriture : en-tête dédié + JSON (le serveur vérifie aussi l'origine).
        headers: { "Content-Type": "application/json", "X-OmniHub-CSRF": "1", ...enteteTenant(tenantId), ...(token && { Authorization: `Bearer ${token}` }) },
      });
      const corps = await res.json().catch(() => ({}));
      if (res.ok) {
        setFait(corps.nom || "attestation");
        toast.success("Attestation générée et rangée dans les pièces du dossier");
        onUpdate?.();
        return;
      }
      const d = corps.detail;
      if (d && typeof d === "object") {
        setRefus({ message: d.message || "Génération impossible", manquants: Array.isArray(d.manquants) ? d.manquants : [] });
      } else {
        setRefus({ message: res.status === 403 ? "Réservé aux administrateurs, avec le module CEE activé" : "Génération impossible", manquants: [] });
      }
    } catch {
      setRefus({ message: "Erreur réseau : l'attestation n'a pas été générée", manquants: [] });
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div data-testid="attestation-honneur">
      <button type="button" onClick={generer} disabled={disabled || enCours} data-testid="attestation-honneur-btn"
        className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium"
        style={{ background: "transparent", border: "1px solid var(--oh-accent)", color: "var(--oh-accent-text)",
                 opacity: disabled || enCours ? 0.6 : 1, cursor: disabled || enCours ? "not-allowed" : "pointer" }}>
        {enCours ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <FileText className="w-4 h-4" aria-hidden="true" />}
        Générer l'attestation
      </button>
      {fait && (
        <p className="text-xs mt-2 rounded-lg px-3 py-2" data-testid="attestation-honneur-ok" role="status"
          style={{ background: "var(--oh-success-bg)", color: "var(--oh-success-text)", border: "1px solid var(--oh-success-border)" }}>
          Pièce rangée dans les documents du dossier : {fait}
        </p>
      )}
      {refus && (
        <div className="text-xs mt-2 rounded-lg px-3 py-2" data-testid="attestation-honneur-refus" role="alert"
          style={{ background: "var(--oh-error-bg)", color: "var(--oh-error-text)", border: "1px solid var(--oh-error-border)" }}>
          <p>{refus.message}</p>
          {refus.manquants.length > 0 && (
            <ul className="mt-1 list-disc pl-4">
              {refus.manquants.map((m, i) => <li key={i}>{m.donnee}{m.ou ? ` — ${m.ou}` : ""}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
